import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import { useKeptVersion } from 'api/keptVersion';
import { FormQuestion } from 'components/survey/form-questions-list/FormQuestionsList';
import useNavigationContext from 'hooks/context/nav-context';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CustomerResource } from 'types/apiTypes';
import { v4 as uuid } from 'uuid';
import { useSymplCookie } from 'hooks/symplCookie';
import { GET_VACANCY_SURVEY } from 'graphql/vacancies/queries';
import { useQuery } from 'hooks/sympl-query';
import {
  UPDATE_VACANCY_QUESTIONS,
  UPDATE_VACANCY_SURVEY,
} from 'graphql/vacancies/mutations';
import { useMutation } from 'hooks/sympl-mutation';
import { AutoSaveState, useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { usePendingSave } from 'context/PendingSaveContext';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import { WORK_WAS_KEPT } from 'api/waitingChanges';
import useGetStartedContext from 'hooks/context/get-started-context';
import {
  lookBody,
  lookPath,
  questionsBody,
  questionsPath,
  questionsToStore,
  saveLook,
  saveQuestions,
} from 'api/interviewAutoSave';
import { ApolloError } from '@apollo/client';
import { ToastTypes } from 'types/notificationTypes';
import { useToastNotifications } from 'hooks/notificationHooks';

type ISurveyContext = {
  questions: FormQuestion[];
  currentOpenQuestion?: string;
  typeformUrl?: string;
  updatingVacancySurvey: boolean;
  updateVacancySurveyError?: ApolloError;
  surveyData?: SurveyQuestionsPayload;
  loadingQuestions: boolean;
  logo: CustomerResource | null;
  primaryColor?: string;
  secondaryColor?: string;
  hasUnsavedChanges: boolean;
  /** Auto-save: the screen stores itself (no Save button). */
  storesItself: boolean;
  /** A question's form says whether it holds words not confirmed yet. */
  setUnconfirmed: (unconfirmed: boolean) => void;
  /** Why the logo and colours on screen are not stored, if they are not. */
  lookNotStored?: string;
  saveState: AutoSaveState;
  keptAt: Date | null;
  switching: boolean;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  setPrimaryColor: (color: string) => void;
  setSecondaryColor: (color: string) => void;
  setLogo: (logo: CustomerResource | null) => void;
  setQuestions: (questions: FormQuestion[]) => void;
  changeQuestion: (question: FormQuestion) => void;
  createQuestion: () => void;
  deleteQuestion: (questionRef: FormQuestion['ref']) => void;
  cancelEditQuestion: () => void;
  onSave: () => Promise<void>;
  questionClicked: (questionRef?: string) => void;
};

export const SurveyContext = createContext<ISurveyContext>(undefined!);

interface SurveyQuestionsPayload {
  vacSurvey?: {
    id: number;
    logo_id: number | null;
    primary_color: string | null;
    secondary_color: string | null;
    questions: FormQuestion[];
  };
}

interface AllQuestionsMutationPayload {
  vacancyId: number;
  input: {
    survey_questions: FormQuestion[];
  };
}

interface UpdateVacancySurveyPayload {
  logo_id: number;
  primary_color: string | null;
  secondary_color: string | null;
}

export const SurveyContextProvider: React.FC<React.ReactNode> = ({
  children,
}) => {
  const {
    activeVacancy,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    currentVacancy,
  } = useNavigationContext();
  const { addToast } = useToastNotifications();

  const [questions, _setQuestions] = useState<FormQuestion[]>([]);
  const [logo, setLogo] = useState<CustomerResource | null>(null);
  const [primaryColor, setPrimaryColor] = useState<string>();
  const [secondaryColor, setSecondaryColor] = useState<string>();
  const [cookie, setCookie] = useSymplCookie<number[]>('viewed-surveys');

  const [currentOpenQuestion, setCurrentOpenQuestion] = useState<string>();
  const createQuestionInProgress = useRef(false);

  const {
    loading: loadingQuestions,
    data: surveyData,
    refetch: refetchQuestions,
  } = useQuery<SurveyQuestionsPayload, { vacancyId: number }>(
    GET_VACANCY_SURVEY,
    {
      skip: !activeVacancy,
      /*
       * Auto-save: always from the server, never the browser's remembered
       * copy. The screen no longer loads the questions again after a store,
       * so the remembered copy is the one from when the screen opened;
       * shown again after a campaign switch, it brought the old questions
       * back and the next change stored them over the newer work (review of
       * the slice).
       */
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: { vacancyId: activeVacancy ?? 0 },
    }
  );

  const [updateAllQuestions] = useMutation<
    undefined,
    AllQuestionsMutationPayload
  >(UPDATE_VACANCY_QUESTIONS, {
    awaitRefetchQueries: true,
  });

  const [
    updateVacancySurvey,
    { loading: updatingVacancySurvey, error: updateVacancySurveyError },
  ] = useMutation<
    undefined,
    {
      vacancySurveyId: number;
      input: UpdateVacancySurveyPayload;
    }
  >(UPDATE_VACANCY_SURVEY);

  const typeformUrl = useMemo(
    () => currentVacancy?.typeform_url,
    [currentVacancy?.typeform_url]
  );

  /*
   * Auto-save. The screen stores itself on every campaign: on one that was
   * never published (in sympl only: its outside form does not exist yet),
   * on one whose job is OPEN (kept; the outside form is written at "Publish
   * changes"), and on one that is CLOSED (kept; the outside form is written
   * when the campaign is re-opened; Steven, 5 Oct 2026).
   *
   * `loadedFor` is the campaign the questions on screen were loaded for:
   * picking another campaign does not rebuild this screen at once.
   */
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();
  const { refetchCheckList } = useGetStartedContext();
  const [loadedFor, setLoadedFor] = useState<number | null>(null);
  const [loads, setLoads] = useState(0);
  /**
   * A question's little form holds words that are not confirmed with
   * "Update" yet. They are not part of the screen's work, so they cannot be
   * stored; leaving with them asks first, as for work that is not saved.
   */
  const [unconfirmed, setUnconfirmed] = useState(false);
  const unconfirmedNow = useRef(false);
  unconfirmedNow.current = unconfirmed;
  const storesItself =
    !!activeVacancy &&
    loadedFor === activeVacancy &&
    !!currentVacancy &&
    currentVacancy.key === activeVacancy;
  const storesItselfNow = useRef(false);
  storesItselfNow.current = storesItself;
  /** The old "unsaved changes" mark belongs to the Save button only. */
  const markUnsaved = (unsaved: boolean) =>
    setHasUnsavedChanges(unsaved && !storesItselfNow.current);
  useEffect(() => {
    if (storesItself) setHasUnsavedChanges(false);
  }, [storesItself, setHasUnsavedChanges]);

  const setQuestions = (updatedQuestions: FormQuestion[]) => {
    _setQuestions(updatedQuestions);
    if (questions.length > 0 && updatedQuestions !== questions)
      markUnsaved(true);
  };

  useEffect(() => {
    (async () => await refetchQuestions())();
  }, [refetchQuestions]);

  useEffect(() => {
    const rawQuestions = surveyData?.vacSurvey?.questions ?? [];
    // Deduplicate by ref (keep first occurrence to preserve order) - guards against API/cache returning duplicates
    const seenRefs = new Set<string>();
    const deduped = rawQuestions.filter((q) => {
      const ref = q?.ref;
      if (!ref || seenRefs.has(ref)) return false;
      seenRefs.add(ref);
      return true;
    });
    const loaded = deduped.map((question) => ({
      ...question,
      required: !!question?.required,
      editable: !!question?.editable,
      custom: !!question?.custom,
      delete: false,
    }));
    setQuestions(loaded);
    // What was loaded is what the server holds.
    if (surveyData?.vacSurvey) {
      // A fresh load is not a step of the person's: Undo starts again.
      setLoads((n) => n + 1);
      stored.current = {
        questions: JSON.stringify(questionsToStore(loaded)),
        look: JSON.stringify([
          surveyData.vacSurvey.logo_id ?? null,
          surveyData.vacSurvey.primary_color ?? null,
          surveyData.vacSurvey.secondary_color ?? null,
        ]),
        surveyId: surveyData.vacSurvey.id,
      };
      if (activeVacancy) storedFor.current.set(activeVacancy, stored.current);
      setLoadedFor(activeVacancy ?? null);
    }
    return () => setQuestions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyData]);

  const initLogo = useMemo(() => {
    return {
      id: surveyData?.vacSurvey?.logo_id,
    } as CustomerResource;
  }, [surveyData?.vacSurvey]);

  useEffect(() => {
    if (initLogo?.id !== logo?.id) markUnsaved(true);
    else setHasUnsavedChanges(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logo, initLogo, setHasUnsavedChanges]);

  const changeQuestion = (question: FormQuestion) => {
    const hasMatch = questions.some((q) => q.ref === question.ref);
    if (!hasMatch) return;

    // Replace ALL questions with this ref (handles any duplicate refs)
    const updatedQuestions = questions.map((q) =>
      q.ref === question.ref ? { ...question } : q
    );
    setQuestions(updatedQuestions);
    setCurrentOpenQuestion(undefined);
  };

  /**
   * Creates a new question (with a unique uuid)
   * and appends it to the current question array.
   * Uses functional update to avoid stale state and guards against double-clicks.
   */
  const createQuestion = useCallback(() => {
    if (createQuestionInProgress.current) return;
    createQuestionInProgress.current = true;

    const ref = uuid();
    const newQuestion: FormQuestion = {
      label: '',
      type: 'long_text',
      index: 3,
      required: true,
      editable: true,
      custom: true,
      delete: false,
      ref,
    };

    _setQuestions((prev) => {
      const zipIndex = prev.findIndex((q) => q.ref === 'zipcode');
      const updated = [...prev];
      updated.splice(zipIndex + 1, 0, newQuestion);
      return updated;
    });
    markUnsaved(true);
    questionClicked(ref);

    // Reset guard after a short delay (allows React to process state update)
    requestAnimationFrame(() => {
      createQuestionInProgress.current = false;
    });
  }, [setHasUnsavedChanges]);

  /**
   * Closes all questions except current one or
   * toggles the questions if it is already active
   */
  const questionClicked = (questionRef?: string) => {
    setCurrentOpenQuestion(questionRef);
  };

  /** Removes a question from the question array */
  const deleteQuestion = (questionRef: FormQuestion['ref']) => {
    const updatedQuestions = questions.map((q) =>
      q.ref === questionRef ? { ...q, delete: true } : q
    );
    setQuestions(updatedQuestions);
    setCurrentOpenQuestion(undefined);
  };

  /** Inactivates all questions */
  const cancelEditQuestion = () => setCurrentOpenQuestion(undefined);

  const onSave = async () => {
    if (!activeVacancy) return Promise.reject();

    const toSave = questions.filter(({ label }) => label !== '');
    // Deduplicate by ref: preserve order of first occurrence, use last occurrence's data.
    // Final filter ensures no duplicate refs are ever sent to the backend.
    const order: FormQuestion[] = [];
    const byRef = new Map<string, FormQuestion>();
    toSave.forEach((q) => {
      if (!byRef.has(q.ref)) order.push(q);
      byRef.set(q.ref, q);
    });
    const deduped = order.map((q) => byRef.get(q.ref)!);
    const seenRefs = new Set<string>();
    const surveyQuestions = deduped.filter((q) => {
      if (seenRefs.has(q.ref)) return false;
      seenRefs.add(q.ref);
      return true;
    });

    await updateAllQuestions({
      variables: {
        vacancyId: activeVacancy!,
        input: { survey_questions: surveyQuestions },
      },
    });

    if (logo) {
      await updateVacancySurvey({
        variables: {
          vacancySurveyId: surveyData?.vacSurvey?.id ?? 0,
          input: {
            logo_id: logo.id,
            primary_color: primaryColor ?? null,
            secondary_color: secondaryColor ?? null,
          },
        },
      });
    }

    addToast({
      type: ToastTypes.SUCCESS,
      description: 'The survey has been saved',
    });

    refetchQuestions();
    return Promise.resolve();
  };

  useEffect(() => {
    return () => setHasUnsavedChanges(false);
  }, [setHasUnsavedChanges]);

  /** What the server holds of this screen, as far as the screen knows. */
  interface Held {
    questions: string;
    look: string;
    surveyId: number | null;
  }
  const stored = useRef<Held>({ questions: '', look: '', surveyId: null });
  /** The same per campaign: a send for the campaign just left uses ITS record. */
  const storedFor = useRef(new Map<number, Held>());

  /** The screen's work: what is stored, and what Undo puts back. */
  interface InterviewWork {
    questions: FormQuestion[];
    logo: CustomerResource | null;
    primaryColor: string | null;
    secondaryColor: string | null;
  }
  const work = useMemo<InterviewWork | undefined>(
    () =>
      storesItself
        ? {
            questions,
            logo,
            primaryColor: primaryColor ?? null,
            secondaryColor: secondaryColor ?? null,
          }
        : undefined,
    [storesItself, questions, logo, primaryColor, secondaryColor]
  );
  /** Only what is stored: a question without wording is not, nor a logo's details. */
  const printOf = (value: InterviewWork) => ({
    questions: questionsToStore(value.questions),
    look: [value.logo?.id ?? null, value.primaryColor, value.secondaryColor],
  });
  const storable = useMemo(() => (work ? printOf(work) : undefined), [work]);
  /*
   * The server stores logo and colours together and only WITH a logo. So
   * colours changed without a logo, or a logo taken away, cannot be stored.
   * The corner says so instead of a bare "Saved at" (review of the slice).
   */
  const lookNotStored =
    storable &&
    !storable.look[0] &&
    JSON.stringify(storable.look) !==
      (storedFor.current.get(activeVacancy ?? 0)?.look ?? '')
      ? 'logo and colours not saved: choose a logo'
      : undefined;

  // D10: the count of stores this screen builds on, per campaign.
  const version = useKeptVersion('interview', activeVacancy ?? null);

  const storeInterview = useCallback(
    async (
      value: ReturnType<typeof printOf>,
      signal?: AbortSignal
    ): Promise<string> => {
      if (!activeVacancy) throw new Error('No campaign is open.');
      const held = storedFor.current.get(activeVacancy);
      if (!held) throw new Error('This campaign is not loaded yet.');
      // D10: questions and look are one thing, stored one after the other.
      const campaign = activeVacancy;
      const guard = () => ({
        basedOn: version.basedOn(campaign),
        stored: (count: number | null) => version.stored(campaign, count),
      });

      const questionsNow = JSON.stringify(value.questions);
      if (questionsNow !== held.questions) {
        await saveQuestions(activeVacancy, value.questions, signal, guard());
        held.questions = questionsNow;
      }

      const lookNow = JSON.stringify(value.look);
      const [logoId, primary, secondary] = value.look;
      // The look is stored with its logo: without one there is nothing to store.
      if (lookNow !== held.look && logoId && held.surveyId) {
        await saveLook(
          held.surveyId,
          {
            logo_id: logoId as number,
            primary_color: (primary as string | null) ?? null,
            secondary_color: (secondary as string | null) ?? null,
          },
          signal,
          // Built on the count the questions' store has just answered.
          guard()
        );
        held.look = lookNow;
      }

      return new Date().toISOString();
    },
    [activeVacancy]
  );

  /** As the tab goes, or the campaign changes: what is still on its way. */
  const sendOnTheWayOut = (
    campaign: number,
    value: ReturnType<typeof printOf>
  ) => {
    const held = storedFor.current.get(campaign);
    if (!held) return;
    if (JSON.stringify(value.questions) !== held.questions) {
      sendKeepaliveSave(
        questionsPath(campaign),
        questionsBody(value.questions),
        'POST',
        version.basedOn(campaign)
      );
    }
    const [logoId, primary, secondary] = value.look;
    if (JSON.stringify(value.look) !== held.look && logoId && held.surveyId) {
      sendKeepaliveSave(
        lookPath(held.surveyId),
        lookBody({
          logo_id: logoId as number,
          primary_color: (primary as string | null) ?? null,
          secondary_color: (secondary as string | null) ?? null,
        }),
        'PUT',
        version.basedOn(campaign)
      );
    }
  };

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<ReturnType<typeof printOf>>({
    value: storable,
    enabled: storesItself,
    identity: activeVacancy ? `interview:${activeVacancy}` : null,
    save: storeInterview,
    flushOnHide: (value) => {
      if (activeVacancy) sendOnTheWayOut(activeVacancy, value);
    },
    // The questions on screen at a campaign change are the previous
    // campaign's, and so is what the server holds of them.
    flushOnIdentityChange: (previous, value) => {
      const campaign = Number(String(previous).replace('interview:', ''));
      if (campaign) sendOnTheWayOut(campaign, value);
    },
  });

  /** One undo step puts a whole earlier screen back (D11, D13). */
  const { canUndo, canRedo, undo, redo } = useUndoRedo<{
    print: string;
    work: InterviewWork;
  }>({
    value:
      work && storable
        ? { print: JSON.stringify(printOf(work)), work }
        : undefined,
    enabled: storesItself,
    identity: activeVacancy ? `${activeVacancy}:${loads}` : null,
    same: (a, b) => a.print === b.print,
    apply: (earlier) => {
      _setQuestions(earlier.work.questions);
      setLogo(earlier.work.logo);
      setPrimaryColor(earlier.work.primaryColor ?? undefined);
      setSecondaryColor(earlier.work.secondaryColor ?? undefined);
      setCurrentOpenQuestion(undefined);
    },
  });

  // What the sidebar and the campaign picker ask before taking the person away.
  useEffect(() => {
    if (!storesItself) return undefined;
    setPending((hasPending && hasFailed) || unconfirmed);
    registerAbandon(abandon);
    registerSaveNow(
      // Whatever CAN be stored is stored; words still in an open question
      // form cannot, so the answer is "not kept" and the person is asked.
      () => (unconfirmedNow.current ? saveNow().then(() => false) : saveNow()),
      () => !unconfirmedNow.current && isKept()
    );
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
  }, [
    storesItself,
    hasPending,
    hasFailed,
    unconfirmed,
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
  ]);

  // What used to follow the Save button follows a save that landed. The
  // questions are NOT loaded again: that would rebuild the list under the
  // person's hands.
  useEffect(() => {
    if (!storesItself || saveState !== 'saved' || hasPending) return;
    refetchCheckList();
    window.dispatchEvent(new CustomEvent(WORK_WAS_KEPT));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState, hasPending]);

  useEffect(() => {
    setHasUnsavedChanges(false);
  }, []);

  useEffect(() => {
    if (!surveyData) return;

    // Always all three, also when this campaign has none: what stayed on
    // screen from the campaign before was stored onto this one by the next
    // change (review of the slice).
    setLogo(
      surveyData.vacSurvey?.logo_id
        ? ({ id: surveyData.vacSurvey.logo_id } as CustomerResource)
        : null
    );
    setPrimaryColor(surveyData.vacSurvey?.primary_color ?? undefined);
    setSecondaryColor(surveyData.vacSurvey?.secondary_color ?? undefined);
  }, [surveyData]);

  useEffect(() => {
    if (!activeVacancy) return;

    // First vacancy -> initialize cookie
    if (cookie === undefined) {
      setCookie([activeVacancy]);
      return;
    }

    // Vacancy not in cookie -> add it
    if (!cookie.includes(activeVacancy)) setCookie([...cookie, activeVacancy]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookie]);
  return (
    <SurveyContext.Provider
      value={{
        questions,
        currentOpenQuestion,
        typeformUrl,
        updatingVacancySurvey,
        updateVacancySurveyError,
        surveyData,
        loadingQuestions,
        logo,
        primaryColor,
        secondaryColor,
        hasUnsavedChanges,
        storesItself,
        setUnconfirmed,
        lookNotStored,
        saveState,
        keptAt,
        switching,
        canUndo,
        canRedo,
        undo,
        redo,
        setQuestions,
        changeQuestion,
        createQuestion,
        deleteQuestion,
        cancelEditQuestion,
        onSave,
        setLogo,
        setPrimaryColor,
        setSecondaryColor,
        questionClicked,
      }}
    >
      {children}
      <OutOfDateBox
        refusal={refusal}
        onLoadTheirs={() => {
          abandon();
          window.setTimeout(() => window.location.reload(), 100);
        }}
        onKeepMine={() => {
          version.keepMine();
          retryRefused();
        }}
      />
    </SurveyContext.Provider>
  );
};
